// src/components/Pentests/tabs/tools/ZapLauncher.js
//
// Dedicated ZAP-only launcher. Shipped 2026-05-18 alongside Phase 3 beacon
// profiles, where ZAP also runs on the new `dast` beacon profile in addition
// to the existing pentester profile.
//
// Same backend path as the multi-engine VulnScanLauncher: creates a VulnScan
// with engines=[{engine:'zap', profile_name}] and runs it. Power users who
// want nuclei + ZAP in one combined run still use Tools > Vulnerability Scan.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Bug, Play } from 'lucide-react';
import { vulnScanAPI } from '../../../../services/api';
import { PrimaryCTA } from '../../../common/Shinobi';
import ProfileCombobox from './ProfileCombobox';

export default function ZapLauncher({ pentest }) {
  const { id: pentestIdParam } = useParams();
  const pentestId = Number(pentestIdParam);
  const navigate = useNavigate();

  const [profiles, setProfiles] = useState([]);
  const [profileName, setProfileName] = useState('');
  const [targetsText, setTargetsText] = useState('');
  const [scanName, setScanName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    vulnScanAPI
      .getProfiles()
      .then((resp) => {
        if (cancelled) return;
        const list = Array.isArray(resp?.data) ? resp.data : (resp?.data?.profiles || []);
        const zapOnly = list.filter((p) => (p.engine || 'nuclei') === 'zap');
        setProfiles(zapOnly);
        // Default to the first non-custom profile, fall back to first available.
        const pick = zapOnly.find((p) => p.name !== 'custom') || zapOnly[0];
        if (pick) setProfileName(pick.name || pick.id);
      })
      .catch((e) => !cancelled && setError(e?.response?.data?.detail || e.message));
    return () => { cancelled = true; };
  }, []);

  const scopeChips = useMemo(() => {
    const sc = pentest?.scope_targets || {};
    return [
      ...(sc.urls || []),
      ...(sc.hostnames || []),
    ].filter(Boolean);
  }, [pentest]);

  const estimatedMinutes = useMemo(() => {
    const prof = profiles.find((p) => p.name === profileName);
    return prof?.estimated_duration_minutes || null;
  }, [profiles, profileName]);

  const appendChip = (val) => {
    setTargetsText((prev) => (prev ? `${prev}\n${val}` : val));
  };

  const onLaunch = async (e) => {
    e.preventDefault();
    const targetList = targetsText
      .split('\n')
      .map((t) => t.trim())
      .filter(Boolean);
    if (targetList.length === 0) {
      setError('Add at least one URL or hostname');
      return;
    }
    if (!profileName) {
      setError('Pick a ZAP profile');
      return;
    }
    setSubmitting(true);
    setError(null);
    setInfo(null);
    try {
      const body = {
        engines: [{ engine: 'zap', profile_name: profileName }],
        targets: targetList,
        name: scanName.trim() || undefined,
        pentest_id: pentestId,
      };
      const created = await vulnScanAPI.create(body);
      const vulnScanId = created?.data?.id;
      if (!vulnScanId) throw new Error('No id in vuln-scan create response');

      const runResp = await vulnScanAPI.run(vulnScanId, { pentest_id: pentestId });
      const runId = runResp?.data?.run_id;
      setInfo(`OWASP ZAP scan #${vulnScanId} launched (run #${runId}). View in the Runs tab.`);
      setTargetsText('');
      setScanName('');
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onLaunch} className="space-y-5 max-w-2xl">
      <div className="flex items-start gap-3 rounded-lg border border-orange-300 bg-orange-500/5 p-3">
        <div className="flex items-center justify-center w-9 h-9 rounded-lg border border-orange-400/60 bg-orange-100 text-orange-700 shrink-0">
          <Bug className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-900">OWASP ZAP</div>
          <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">
            Active web app scanner. Spider + ascan against each URL. Routes to a beacon with the
            <code className="mx-1 px-1 rounded bg-cyber-100 text-slate-700">zap_scan</code>
            capability (pentester or dast profile).
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-800 mb-2">Profile</label>
        <ProfileCombobox
          profiles={profiles}
          value={profileName}
          onChange={setProfileName}
        />
        {estimatedMinutes && (
          <div className="mt-1.5 text-xs text-slate-500">
            Estimated ~{estimatedMinutes} min per target.
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-800 mb-2">
          Targets (one per line)
        </label>
        <textarea
          value={targetsText}
          onChange={(e) => setTargetsText(e.target.value)}
          rows={5}
          placeholder={'https://app.example.com\napi.example.com'}
          className="w-full bg-cyber-100 border border-cyber-300/60 rounded-lg px-3 py-2 text-slate-900 focus:border-shinobi-green outline-none font-mono text-sm"
          required
        />
        {scopeChips.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            <span className="text-xs text-slate-500 mr-1 self-center">Add from scope:</span>
            {scopeChips.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => appendChip(t)}
                className="px-2 py-0.5 text-xs rounded border border-cyber-300/60 bg-cyber-100 text-slate-700 hover:border-shinobi-green hover:text-shinobi-green"
              >
                {t}
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-800 mb-2">
          Scan Name (optional)
        </label>
        <input
          type="text"
          value={scanName}
          onChange={(e) => setScanName(e.target.value)}
          placeholder="Default: auto-generated from profile + timestamp"
          className="w-full bg-cyber-100 border border-cyber-300/60 rounded-lg px-3 py-2 text-slate-900 focus:border-shinobi-green outline-none"
        />
      </div>

      {error && <div className="text-red-600 text-sm">{error}</div>}
      {info && (
        <div className="text-shinobi-green text-sm">
          {info}{' '}
          <button
            type="button"
            onClick={() => navigate(`/pentests/${pentestId}/runs`)}
            className="underline"
          >
            Open Runs
          </button>
        </div>
      )}

      <div className="flex items-center justify-end">
        <PrimaryCTA
          variant="green"
          type="submit"
          loading={submitting}
          icon={Play}
          iconPosition="left"
          disabled={profiles.length === 0 || !profileName}
        >
          Launch ZAP Scan
        </PrimaryCTA>
      </div>
    </form>
  );
}
